import React, { useState, useEffect } from 'react';
import { Briefcase, DollarSign, Clock, Star, Users, TrendingUp, Calendar, Award, Bell, Mail, Phone, Save, Filter } from 'lucide-react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import ProfitLineGraph from '../components/ProfitLineGraph';
import { BalancesPanel } from '../components/Admin/BalancesPanel';

const API = '/api';

const EmployeeDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [notificationEmail, setNotificationEmail] = useState('');
  const [notificationPhone, setNotificationPhone] = useState('');
  const [savingPreferences, setSavingPreferences] = useState(false);
  const [profitData, setProfitData] = useState([]);
  const [profitLoading, setProfitLoading] = useState(false);
  const [dateFilter, setDateFilter] = useState({ start: '', end: '' });
  const navigate = useNavigate();

  useEffect(() => {
    // Check if user is logged in and is an employee
    const userData = localStorage.getItem('user_data');
    if (!userData) {
      navigate('/');
      return;
    }

    const parsedUser = JSON.parse(userData);
    setUser(parsedUser);

    if (!parsedUser.is_employee || !parsedUser.engineer_id) {
      navigate('/');
      return;
    }

    fetchStats(parsedUser.engineer_id);
    fetchProfitData(parsedUser.engineer_id);
  }, [navigate]);

  const fetchStats = async (engineerId) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/employees/stats/${engineerId}`);
      setStats(response.data);
      
      // Set notification preferences if they exist
      if (response.data.notification_email) {
        setNotificationEmail(response.data.notification_email);
      }
      if (response.data.notification_phone) {
        setNotificationPhone(response.data.notification_phone);
      }
    } catch (error) {
      console.error('Error fetching employee stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProfitData = async (engineerId, startDate = null, endDate = null) => {
    try {
      setProfitLoading(true);
      let url = `${API}/analytics/profit-graph?engineer_id=${engineerId}`;
      if (startDate) url += `&start_date=${startDate}`;
      if (endDate) url += `&end_date=${endDate}`;
      
      const response = await axios.get(url);
      setProfitData(response.data.data);
    } catch (error) {
      console.error('Error fetching profit data:', error);
      toast.error('Failed to load profit data');
    } finally {
      setProfitLoading(false);
    }
  };

  const handleDateFilterApply = () => {
    if (user && user.engineer_id) {
      fetchProfitData(user.engineer_id, dateFilter.start, dateFilter.end);
    }
  };

  const handleClearFilter = () => {
    setDateFilter({ start: '', end: '' });
    if (user && user.engineer_id) {
      fetchProfitData(user.engineer_id);
    }
  };

  const handleSavePreferences = async () => {
    try {
      setSavingPreferences(true);
      const response = await axios.post(
        `${API}/employees/notification-preferences/${user.engineer_id}`,
        {
          notification_email: notificationEmail || null,
          notification_phone: notificationPhone || null
        }
      );
      toast.success('Notification preferences saved!');
    } catch (error) {
      console.error('Error saving preferences:', error);
      toast.error('Failed to save preferences');
    } finally {
      setSavingPreferences(false);
    }
  };

  if (loading || !stats) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500 mb-4"></div>
          <p className="text-gray-400">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-900 to-pink-900 border-b border-purple-500/30 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-3">
                <Briefcase size={32} className="text-purple-300" />
                {stats.engineer_name}
              </h1>
              <p className="text-purple-200 mt-1">Employee Dashboard</p>
            </div>
            <button
              onClick={() => navigate('/')}
              className="px-4 py-2 bg-purple-600/20 hover:bg-purple-600/30 border border-purple-500/30 rounded-lg transition-colors"
            >
              ← Back to Home
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6 space-y-6">
        <BalancesPanel auth={{ token: localStorage.getItem('auth_token') }} title="Your clients' open balances" />
        {/* Key Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* This Month Revenue */}
          <div className="bg-gradient-to-br from-green-900/30 to-emerald-900/30 border border-green-500/30 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <DollarSign className="text-green-400" size={32} />
              <span className="text-xs text-green-400 bg-green-500/20 px-2 py-1 rounded-full">This Month</span>
            </div>
            <h3 className="text-2xl font-bold text-white">${stats.this_month_revenue}</h3>
            <p className="text-gray-400 text-sm mt-1">Collected · booked ${stats.this_month_booked ?? 0}</p>
            <p className="text-green-400 text-xs mt-2">{stats.this_month_bookings} bookings</p>
          </div>

          {/* This Month Hours */}
          <div className="bg-gradient-to-br from-blue-900/30 to-cyan-900/30 border border-blue-500/30 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <Clock className="text-blue-400" size={32} />
              <span className="text-xs text-blue-400 bg-blue-500/20 px-2 py-1 rounded-full">This Month</span>
            </div>
            <h3 className="text-2xl font-bold text-white">{stats.this_month_hours}h</h3>
            <p className="text-gray-400 text-sm mt-1">Hours Worked</p>
          </div>

          {/* Average Rating */}
          <div className="bg-gradient-to-br from-yellow-900/30 to-orange-900/30 border border-yellow-500/30 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <Star className="text-yellow-400" size={32} />
              <span className="text-xs text-yellow-400 bg-yellow-500/20 px-2 py-1 rounded-full">Rating</span>
            </div>
            <h3 className="text-2xl font-bold text-white">{stats.average_rating}/5</h3>
            <p className="text-gray-400 text-sm mt-1">Average Rating</p>
            <p className="text-yellow-400 text-xs mt-2">{stats.total_reviews} reviews</p>
          </div>

          {/* Total Bookings */}
          <div className="bg-gradient-to-br from-purple-900/30 to-pink-900/30 border border-purple-500/30 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <Award className="text-purple-400" size={32} />
              <span className="text-xs text-purple-400 bg-purple-500/20 px-2 py-1 rounded-full">All Time</span>
            </div>
            <h3 className="text-2xl font-bold text-white">{stats.total_bookings}</h3>
            <p className="text-gray-400 text-sm mt-1">Total Bookings</p>
            <p className="text-purple-400 text-xs mt-2">{stats.total_hours}h total</p>
          </div>
        </div>

        {/* Profit Graph Section */}
        <div className="bg-gradient-to-br from-gray-900 to-black border border-purple-500/20 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold flex items-center gap-2">
              <TrendingUp className="text-purple-400" size={24} />
              Your Profit Over Time
            </h2>
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={dateFilter.start}
                onChange={(e) => setDateFilter({ ...dateFilter, start: e.target.value })}
                className="px-3 py-2 bg-black/40 border border-purple-500/30 rounded-lg text-white text-sm focus:outline-none focus:border-purple-500"
                placeholder="Start Date"
              />
              <span className="text-gray-500">to</span>
              <input
                type="date"
                value={dateFilter.end}
                onChange={(e) => setDateFilter({ ...dateFilter, end: e.target.value })}
                className="px-3 py-2 bg-black/40 border border-purple-500/30 rounded-lg text-white text-sm focus:outline-none focus:border-purple-500"
                placeholder="End Date"
              />
              <button
                onClick={handleDateFilterApply}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-semibold transition-colors flex items-center gap-1"
              >
                <Filter size={16} />
                Apply
              </button>
              {(dateFilter.start || dateFilter.end) && (
                <button
                  onClick={handleClearFilter}
                  className="px-3 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg text-sm transition-colors"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
          {profitLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-purple-500"></div>
            </div>
          ) : (
            <ProfitLineGraph data={profitData} color="#a855f7" />
          )}
        </div>

        {/* Payouts Section */}
        <div className="bg-gradient-to-br from-gray-900 to-black border border-purple-500/20 rounded-xl p-6">
          <h2 className="text-xl font-bold flex items-center gap-2 mb-4">
            <TrendingUp className="text-purple-400" size={24} />
            Monthly Payouts
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-purple-500/20">
                  <th className="text-left py-3 px-4 text-gray-400 font-medium">Month</th>
                  <th className="text-left py-3 px-4 text-gray-400 font-medium">Bookings</th>
                  <th className="text-left py-3 px-4 text-gray-400 font-medium">Hours</th>
                  <th className="text-left py-3 px-4 text-gray-400 font-medium">Revenue</th>
                </tr>
              </thead>
              <tbody>
                {stats.payouts.map((payout, idx) => (
                  <tr key={idx} className="border-b border-purple-500/10 hover:bg-purple-500/5">
                    <td className="py-3 px-4 text-white">{payout.month}</td>
                    <td className="py-3 px-4 text-gray-300">{payout.bookings}</td>
                    <td className="py-3 px-4 text-gray-300">{payout.hours}h</td>
                    <td className="py-3 px-4 text-green-400 font-semibold">${payout.revenue}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Favorite Clients */}
          <div className="bg-gradient-to-br from-gray-900 to-black border border-purple-500/20 rounded-xl p-6">
            <h2 className="text-xl font-bold flex items-center gap-2 mb-4">
              <Users className="text-purple-400" size={24} />
              Favorite Clients
            </h2>
            <div className="space-y-3">
              {stats.favorite_clients.length > 0 ? (
                stats.favorite_clients.map((client, idx) => (
                  <div key={idx} className="bg-purple-900/20 border border-purple-500/20 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-white font-semibold">{client.name}</p>
                        <p className="text-gray-400 text-sm">{client.email}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-purple-400 font-bold">{client.total_bookings}</p>
                        <p className="text-gray-500 text-xs">bookings</p>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-gray-500 text-center py-4">No clients yet</p>
              )}
            </div>
          </div>

          {/* Recent Bookings */}
          <div className="bg-gradient-to-br from-gray-900 to-black border border-purple-500/20 rounded-xl p-6">
            <h2 className="text-xl font-bold flex items-center gap-2 mb-4">
              <Calendar className="text-purple-400" size={24} />
              Recent Bookings
            </h2>
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {stats.recent_bookings.length > 0 ? (
                stats.recent_bookings.map((booking, idx) => (
                  <div key={idx} className="bg-purple-900/20 border border-purple-500/20 rounded-lg p-3">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="text-white font-semibold">{booking.client_name}</p>
                        <p className="text-gray-400 text-sm">{booking.service}</p>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        booking.status === 'confirmed' 
                          ? 'bg-green-500/20 text-green-400' 
                          : 'bg-yellow-500/20 text-yellow-400'
                      }`}>
                        {booking.status}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <span>{booking.date} at {booking.time}</span>
                      <span className="text-green-400 font-semibold">${booking.price}</span>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-gray-500 text-center py-4">No bookings yet</p>
              )}
            </div>
          </div>
        </div>

        {/* Reviews Section */}
        <div className="bg-gradient-to-br from-gray-900 to-black border border-purple-500/20 rounded-xl p-6">
          <h2 className="text-xl font-bold flex items-center gap-2 mb-4">
            <Star className="text-yellow-400" size={24} />
            Your Reviews ({stats.reviews?.length || 0})
          </h2>
          <div className="space-y-4 max-h-96 overflow-y-auto">
            {stats.reviews && stats.reviews.length > 0 ? (
              stats.reviews.map((review, idx) => (
                <div key={idx} className="bg-purple-900/20 border border-purple-500/20 rounded-lg p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="text-white font-semibold">{review.name}</p>
                      <div className="flex items-center gap-1 mt-1">
                        {[...Array(5)].map((_, i) => (
                          <Star
                            key={i}
                            size={16}
                            className={i < review.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-600'}
                          />
                        ))}
                      </div>
                    </div>
                    <span className="text-xs text-gray-500">{review.created_at}</span>
                  </div>
                  <p className="text-gray-300 text-sm">{review.review_text}</p>
                </div>
              ))
            ) : (
              <p className="text-gray-500 text-center py-4">No reviews yet</p>
            )}
          </div>
        </div>

        {/* Most Booked Clients Section */}
        <div className="bg-gradient-to-br from-gray-900 to-black border border-purple-500/20 rounded-xl p-6">
          <h2 className="text-xl font-bold flex items-center gap-2 mb-4">
            <Users className="text-purple-400" size={24} />
            Top Clients (Most Bookings)
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {stats.favorite_clients && stats.favorite_clients.length > 0 ? (
              stats.favorite_clients.map((client, idx) => (
                <div key={idx} className="bg-purple-900/20 border border-purple-500/20 rounded-lg p-4 hover:bg-purple-900/30 transition-colors">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="bg-purple-600/30 rounded-full w-10 h-10 flex items-center justify-center">
                      <span className="text-purple-300 font-bold text-lg">{idx + 1}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-semibold truncate">{client.name}</p>
                      <p className="text-gray-400 text-xs truncate">{client.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-purple-500/20">
                    <span className="text-gray-400 text-sm">Total Bookings</span>
                    <span className="text-purple-400 font-bold text-lg">{client.total_bookings}</span>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-gray-500 text-center py-4 col-span-full">No clients yet</p>
            )}
          </div>
        </div>

        {/* Notification Preferences Section */}
        <div className="bg-gradient-to-br from-gray-900 to-black border border-purple-500/20 rounded-xl p-6">
          <h2 className="text-xl font-bold flex items-center gap-2 mb-4">
            <Bell className="text-purple-400" size={24} />
            Booking Notification Settings
          </h2>
          <p className="text-gray-400 text-sm mb-6">
            Receive notifications when you get new bookings or booking confirmations
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Email Notification */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Notification Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="email"
                  value={notificationEmail}
                  onChange={(e) => setNotificationEmail(e.target.value)}
                  placeholder="your-email@example.com"
                  className="w-full pl-10 pr-4 py-3 bg-black/40 border border-purple-500/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">Email for booking notifications</p>
            </div>

            {/* Phone Notification */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Notification Phone
              </label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="tel"
                  value={notificationPhone}
                  onChange={(e) => setNotificationPhone(e.target.value)}
                  placeholder="+1 (555) 123-4567"
                  className="w-full pl-10 pr-4 py-3 bg-black/40 border border-purple-500/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">SMS notifications (optional)</p>
            </div>
          </div>

          <button
            onClick={handleSavePreferences}
            disabled={savingPreferences}
            className="mt-6 flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-semibold hover:from-purple-700 hover:to-pink-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Save size={20} />
            {savingPreferences ? 'Saving...' : 'Save Notification Settings'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmployeeDashboard;
